import { type CustomerSummary, HttpError, api } from '@/api/client'
import {
  type LineItem,
  LineItemsEditor,
  emptyLine,
  toCreateInvoiceLine,
} from '@/components/finance/LineItemsEditor'
import { CustomerPicker } from '@/components/shared/CustomerPicker'
import { PageHeader } from '@/components/shared/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { useDraftStorage } from '@/hooks/useDraftStorage'
import { useIsReadOnly } from '@/hooks/useSubscription'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import type { NavigateOptions } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route as appLayoutRoute } from '../layout'

interface InvoiceNewSearch {
  description?: string
  amount?: number
  company?: string
  fromQuoteRequest?: string
}

export const Route = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/finance/invoices/new',
  validateSearch: (s: Record<string, unknown>): InvoiceNewSearch => {
    const rawAmount = s.amount
    let amount: number | undefined
    if (typeof rawAmount === 'number' && Number.isFinite(rawAmount)) {
      amount = rawAmount
    } else if (typeof rawAmount === 'string' && rawAmount !== '') {
      const n = Number(rawAmount)
      amount = Number.isFinite(n) ? n : undefined
    }
    return {
      description: typeof s.description === 'string' ? s.description : undefined,
      amount,
      company: typeof s.company === 'string' ? s.company : undefined,
      fromQuoteRequest: typeof s.fromQuoteRequest === 'string' ? s.fromQuoteRequest : undefined,
    }
  },
  component: InvoiceNewPage,
})

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

interface InvoiceDraft {
  customer: CustomerSummary | null
  issueDate: string
  paymentTermsDays: number
  notes: string
  lines: LineItem[]
}

function InvoiceNewPage() {
  const { t } = useTranslation('finance')
  const toast = useToast()
  const qc = useQueryClient()
  const navigate = useNavigate()
  // L7b-bis — freeze les mutations quand le tenant est restricted/suspended.
  const readOnly = useIsReadOnly()
  const search = Route.useSearch() as unknown as InvoiceNewSearch
  const draftStorage = useDraftStorage<InvoiceDraft>('invoice-new')

  // Une recherche explicite (préfill depuis un quote request) prime sur un
  // brouillon resté en localStorage ; sinon on reprend le brouillon le plus récent.
  const draft = search.description ? null : draftStorage.load()

  // Migration brouillons legacy : avant le fix du contrat tax_rate, la valeur
  // était stockée en fraction (0.18) au lieu d'un pourcentage (18). On
  // détecte le cas pour éviter qu'un brouillon ouvert après MAJ envoie 0.18 %.
  if (draft?.lines) {
    for (const ln of draft.lines) {
      if (ln.tax_rate > 0 && ln.tax_rate < 1) {
        ln.tax_rate = Math.round(ln.tax_rate * 100)
      }
    }
  }

  const [customer, setCustomer] = useState<CustomerSummary | null>(draft?.customer ?? null)
  const [issueDate, setIssueDate] = useState(draft?.issueDate ?? todayStr())
  const [paymentTermsDays, setPaymentTermsDays] = useState(draft?.paymentTermsDays ?? 30)
  const [termsTouched, setTermsTouched] = useState(!!draft)
  const [notes, setNotes] = useState(draft?.notes ?? '')

  // Pré-remplit le délai de paiement depuis la fiche client (sauf si l'utilisateur
  // l'a déjà modifié manuellement, ou qu'un brouillon a déjà fixé une valeur),
  // au lieu du 30j générique.
  useEffect(() => {
    if (!customer || termsTouched) return
    api.customers.get(customer.id).then((c) => setPaymentTermsDays(c.payment_terms_days))
  }, [customer, termsTouched])

  const initialLine: LineItem = search.description
    ? {
        _key: crypto.randomUUID(),
        line_type: 'other',
        description: search.description,
        quantity: 1,
        unit_price_xof: search.amount ?? 0,
        tax_rate: 18,
      }
    : emptyLine()

  const [lines, setLines] = useState<LineItem[]>(draft?.lines ?? [initialLine])

  // Sauvegarde best-effort du brouillon à chaque changement, pour ne pas perdre
  // la saisie si l'onglet est fermé avant l'émission de la facture.
  useEffect(() => {
    draftStorage.save({ customer, issueDate, paymentTermsDays, notes, lines })
  }, [customer, issueDate, paymentTermsDays, notes, lines, draftStorage])

  const dueDate = addDays(issueDate, paymentTermsDays)

  const hasValidLines = lines.every((l) => l.description.trim() !== '' && l.unit_price_xof > 0)
  const canSubmit = !!customer && hasValidLines

  const createMutation = useMutation({
    mutationFn: async ({ shouldIssue }: { shouldIssue: boolean }) => {
      const body = {
        customer_id: customer!.id,
        issue_date: issueDate,
        due_date: dueDate,
        payment_terms_days: paymentTermsDays,
        notes: notes.trim() || undefined,
        lines: lines.map(toCreateInvoiceLine),
      }
      const invoice = await api.finance.invoices.create(body)
      if (shouldIssue) {
        return api.finance.invoices.issue(invoice.id)
      }
      return invoice
    },
    onSuccess: (invoice, { shouldIssue }) => {
      draftStorage.clear()
      void qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      toast.show({
        variant: 'success',
        title: shouldIssue
          ? t('invoice.issued_success', { defaultValue: 'Facture émise' })
          : t('invoice.draft_saved', { defaultValue: 'Brouillon enregistré' }),
      })
      navigate({
        to: '/finance/invoices/$invoiceId',
        params: { invoiceId: invoice.id },
      } as unknown as NavigateOptions)
    },
    onError: (err: Error) => {
      const msg =
        err instanceof HttpError
          ? err.message
          : t('error_generic', { defaultValue: 'Une erreur est survenue' })
      toast.show({ variant: 'error', title: msg })
    },
  })

  return (
    <div className="space-y-6">
      <Link
        to="/finance/invoices"
        className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {t('invoices', { defaultValue: 'Factures' })}
      </Link>

      <PageHeader
        title={t('invoice.new', { defaultValue: 'Nouvelle facture' })}
        description={t('invoice.new_description', {
          defaultValue: 'Saisissez les informations de la facture et ses lignes.',
        })}
      />

      {/* Header section */}
      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t('invoice.customer', { defaultValue: 'Client' })}</Label>
              <CustomerPicker
                value={customer}
                onChange={setCustomer}
                placeholder={
                  search.company ??
                  t('invoice.customer_placeholder', { defaultValue: 'Rechercher un client…' })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('invoice.issue_date', { defaultValue: "Date d'émission" })}</Label>
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>
                {t('invoice.payment_terms', { defaultValue: 'Délai de paiement' })}{' '}
                <span className="text-xs font-normal text-muted-foreground">
                  ({t('invoice.due_date', { defaultValue: 'Échéance' })} : {dueDate})
                </span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  value={paymentTermsDays}
                  onChange={(e) => {
                    setTermsTouched(true)
                    setPaymentTermsDays(Math.max(0, Number(e.target.value)))
                  }}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">
                  {t('invoice.days', { defaultValue: 'jours' })}
                </span>
              </div>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t('invoice.notes', { defaultValue: 'Notes' })}</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder={t('invoice.notes_placeholder', {
                  defaultValue: 'Notes internes ou mention sur la facture…',
                })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line items */}
      <Card>
        <CardContent className="p-6">
          <h2 className="mb-4 text-sm font-semibold text-foreground">
            {t('invoice.lines', { defaultValue: 'Lignes de facture' })}
          </h2>
          <LineItemsEditor items={lines} onChange={setLines} />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3">
        <Link to="/finance/invoices">
          <Button variant="ghost">
            {t('invoice.void_modal.cancel', { defaultValue: 'Annuler' })}
          </Button>
        </Link>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={!canSubmit || createMutation.isPending || readOnly}
            title={
              readOnly
                ? 'Compte en lecture seule — régularisez votre abonnement.'
                : undefined
            }
            onClick={() => createMutation.mutate({ shouldIssue: false })}
          >
            {t('invoice.save_draft', { defaultValue: 'Enregistrer brouillon' })}
          </Button>
          <Button
            disabled={!canSubmit || createMutation.isPending || readOnly}
            loading={createMutation.isPending}
            title={
              readOnly
                ? 'Compte en lecture seule — régularisez votre abonnement.'
                : undefined
            }
            onClick={() => createMutation.mutate({ shouldIssue: true })}
          >
            {t('invoice.issue', { defaultValue: 'Émettre la facture' })}
          </Button>
        </div>
      </div>
    </div>
  )
}
